import { useEffect, useRef, useState } from 'react';
import AnimatedNumber from './AnimatedNumber';

/**
 * Progress bar. Animates ONCE from 0 → target on mount; subsequent updates
 * (e.g. score change) snap to the new value WITHOUT re-animating to avoid
 * paint storms on slower mobile devices.
 */
export default function AnimatedProgressBar({
    pct = 0,
    barClassName = 'bg-[#D4A537]',
    height = 8,
    showPercent = false,
    duration = 1200,
    shimmer = false,
    tracksClassName = 'bg-[#4A5568]',
    labelClassName = 'text-xs font-medium text-white tabular-nums w-10 text-right',
    children = null,
    style = {},
}) {
    const target = Math.max(0, Math.min(100, Number(pct) || 0));
    const [width, setWidth] = useState(0);
    const mountedRef = useRef(false);

    useEffect(() => {
        if (!mountedRef.current) {
            mountedRef.current = true;
            const id = requestAnimationFrame(() => setWidth(target));
            return () => cancelAnimationFrame(id);
        }
        setWidth(target);
    }, [target]);

    return (
        <div className="flex items-center gap-2 w-full">
            <div
                className={`relative flex-1 ${tracksClassName} rounded-full overflow-hidden`}
                style={{ height: `${height}px`, ...style }}
            >
                <div
                    className={`absolute inset-y-0 left-0 ${barClassName} rounded-full`}
                    style={{
                        width: `${width}%`,
                        transition: `width ${duration}ms cubic-bezier(0.34, 1.4, 0.64, 1)`,
                    }}
                    data-testid="animated-progress-fill"
                />
            </div>
            {children !== null ? (
                children
            ) : showPercent ? (
                <span className={labelClassName}>
                    <AnimatedNumber value={target} duration={duration} suffix="%" />
                </span>
            ) : null}
        </div>
    );
}
